import React, { memo, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { debounce } from 'Utils/debounce';

import { usePaymentFunctions } from 'Context/RocketPay';

import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { listEmailsForPayments } from 'Containers/RocketPay/actions';
import { PaymentFilters } from 'Components/RocketPay';

const PaymentFiltersContainer = () => {
  const {
    // filter open status
    isEmailFilterOpen,
    isDateRangeFilterOpen,
    setIsEmailFilterOpen,
    setIsDateRangeFilterOpen,
    // list of filters to show
    selectedEmailFilters,
    dateFilterStart,
    dateFilterEnd,
    // saved filters
    savedEmailFilters,
    // filter functions
    onEmailFilterListItemClick,
    applyEmailFilters,
    onChangeDateFilter,
    applyDateFilters,
    clearEmailFilters,
    clearDateFilter,
    isDateFilterApplied,
    emailFilters,
  }: any = usePaymentFunctions();

  const dispatch = useDispatch();

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);

  // search box refs
  const emailSearchRef = useRef(undefined);

  const [emailSearchValue, setEmailSearchValue] = useState('');

  const onEmailFilterClick = useCallback(() => {
    setIsEmailFilterOpen((prevState) => !prevState);
    setIsDateRangeFilterOpen(false);
  }, []);

  const onDateRangeFilterClick = useCallback(() => {
    setIsDateRangeFilterOpen((prevState) => !prevState);
    setIsEmailFilterOpen(false);
  }, []);

  const onApplyFilterButtonClicked = useCallback(
    (e: any) => {
      e.preventDefault();
      const { id } = e.target;

      if (id === 'email') {
        applyEmailFilters();
        setIsEmailFilterOpen(false);
      } else if (id === 'date') {
        applyDateFilters();
        setIsDateRangeFilterOpen(false);
      }
    },
    [applyEmailFilters, applyDateFilters]
  );

  const onClearFilterButtonClicked = useCallback((e: any) => {
    e.preventDefault();
    const { id } = e.target;

    if (id === 'email') {
      clearEmailFilters();
      setIsEmailFilterOpen(false);
    } else if (id === 'date') {
      clearDateFilter();
      setIsDateRangeFilterOpen(false);
    }
  }, []);

  // api call to list filters
  const getEmails = useCallback(
    (search = '') => {
      dispatch(listEmailsForPayments(firstCompanyId, search));
    },
    [firstCompanyId]
  );

  // get email and project filters
  useEffect(() => {
    if (firstCompanyId) {
      getEmails();
    }
  }, [firstCompanyId]);

  // handle search box value change
  const handleEmailSearchValueChange = ({ target: { value } }: any) => {
    if (value.length <= 36) {
      setEmailSearchValue(value);
      if (value.length >= 2) {
        getEmails(value);
      }
      if (value.length === 0) {
        getEmails();
      }
    }
  };

  // debounce function on search value change
  const onChangeEmailSearchValue = useMemo(() => debounce(handleEmailSearchValueChange, 300), [firstCompanyId]);

  const onClickEmailSearchClearButton = useCallback(() => {
    getEmails();
    setEmailSearchValue('');
    emailSearchRef.current.value = '';
    emailSearchRef.current.focus();
  }, [firstCompanyId, emailSearchRef]);

  return (
    <PaymentFilters
      startDate={dateFilterStart}
      endDate={dateFilterEnd}
      isEmailFilterOpen={isEmailFilterOpen}
      isDateRangeFilterOpen={isDateRangeFilterOpen}
      // email filters
      emailFilters={emailFilters}
      selectedEmailFilters={selectedEmailFilters}
      emailSearchRef={emailSearchRef}
      emailSearchValue={emailSearchValue}
      savedEmailFiltersCount={savedEmailFilters.length}
      onChangeEmailSearchValue={onChangeEmailSearchValue}
      // status filters
      // functions
      onEmailFilterClick={onEmailFilterClick}
      onEmailFilterListItemClick={onEmailFilterListItemClick}
      onClickEmailSearchClearButton={onClickEmailSearchClearButton}
      onDateRangeFilterClick={onDateRangeFilterClick}
      onChangeDate={onChangeDateFilter}
      onApplyFilterButtonClick={onApplyFilterButtonClicked}
      onClearFilterButtonClick={onClearFilterButtonClicked}
      onClearDateFilterButtonClick={onClearFilterButtonClicked}
      dateFilterApplied={isDateFilterApplied}
    />
  );
};

const PaymentFiltersContainerMemo = memo(PaymentFiltersContainer, areEqual);

export { PaymentFiltersContainerMemo as PaymentFilters };
